<template>
  <v-dialog v-model="abierto" max-width="980" persistent scrollable>
    <template v-slot:activator="{ on, attrs }">
      <v-btn small text color="primary" v-bind="attrs" v-on="on">
        <v-icon left small>mdi-creation</v-icon>
        Cargar desde PDF / foto
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="subtitle-1 font-weight-bold">
        <v-icon color="#BA6B30" class="mr-2">mdi-creation</v-icon>
        Cargar factura desde un archivo
        <v-spacer></v-spacer>
        <v-btn icon small @click="cerrar"><v-icon small>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Paso 1: archivo -->
        <div v-if="!lectura">
          <p class="text-body-2 grey--text text--darken-1">
            Subí el PDF o una foto de la factura del proveedor. Leo el comprobante, busco el proveedor y los
            productos en tu catálogo y te muestro todo para revisar antes de cargarlo. No se registra nada hasta que
            vos confirmes la compra en el formulario.
          </p>
          <v-file-input
            v-model="archivo"
            accept=".pdf,image/png,image/jpeg,image/webp"
            label="PDF o foto de la factura"
            prepend-icon="mdi-file-document-outline"
            outlined dense show-size
            :disabled="leyendo"
          ></v-file-input>
          <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>
          <div class="d-flex">
            <v-spacer></v-spacer>
            <v-btn depressed color="primary" :disabled="!archivo" :loading="leyendo" @click="leer">Leer factura</v-btn>
          </div>
          <p v-if="leyendo" class="text-caption grey--text mt-2">Leyendo el comprobante… suele tardar entre 8 y 15 segundos.</p>
        </div>

        <!-- Paso 2: revisión -->
        <div v-else>
          <v-alert v-if="!lectura.verificacion.cierra" type="warning" dense text>
            <b>Los números del comprobante no cierran.</b>
            <div v-for="(p, i) in lectura.verificacion.problemas" :key="i" class="text-caption">• {{ p }}</div>
            <div class="text-caption mt-1">Revisá las líneas y totales antes de cargar. Puede ser una lectura imperfecta o una factura mal emitida.</div>
          </v-alert>
          <v-alert v-if="lectura.observaciones" type="info" dense text class="text-caption">{{ lectura.observaciones }}</v-alert>

          <v-row dense>
            <v-col cols="12" md="5">
              <div class="text-caption grey--text">Comprobante</div>
              <div class="text-body-2">
                <b>{{ lectura.comprobante.tipo }} {{ lectura.comprobante.letra }}</b>
                {{ numeroFormateado }} · {{ lectura.comprobante.fecha || "sin fecha" }}
                <span v-if="lectura.comprobante.condicion_venta"> · {{ lectura.comprobante.condicion_venta }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="7">
              <div class="text-caption grey--text">Proveedor</div>
              <div class="text-body-2">
                {{ lectura.proveedor.leido.razon_social }}
                <span v-if="lectura.proveedor.leido.cuit"> · CUIT {{ lectura.proveedor.leido.cuit }}</span>
                <v-chip v-if="lectura.proveedor.leido.cuit && !lectura.proveedor.leido.cuit_valido" x-small color="orange" outlined class="ml-1">CUIT dudoso</v-chip>
              </div>
              <div class="d-flex align-center mt-1">
                <v-select
                  v-model="proveedorId"
                  :items="opcionesProveedor"
                  item-text="nombre" item-value="id"
                  dense outlined hide-details
                  label="Proveedor en el sistema"
                  :append-icon="proveedorId ? 'mdi-check-circle' : 'mdi-alert-circle-outline'"
                  :class="proveedorId ? 'ok' : 'falta'"
                ></v-select>
              </div>
              <div class="text-caption grey--text mt-1" v-if="lectura.proveedor.id">
                Encontrado por {{ lectura.proveedor.por }}.
              </div>
              <div class="text-caption orange--text text--darken-2 mt-1" v-else>
                No lo encontré. Elegí uno de los candidatos o dalo de alta antes de cargar la compra.
              </div>
            </v-col>
          </v-row>

          <v-simple-table dense class="mt-3 lineas">
            <thead>
              <tr>
                <th>En la factura</th>
                <th style="width: 44%">Producto en el sistema</th>
                <th class="text-right">Cant.</th>
                <th class="text-right">Unitario</th>
                <th class="text-right">IVA</th>
                <th class="text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(l, i) in lineas" :key="i" :class="estadoLinea(l)">
                <td>
                  <div class="text-body-2">{{ l.descripcion }}</div>
                  <div class="text-caption grey--text" v-if="l.codigo">cód. {{ l.codigo }}</div>
                </td>
                <td>
                  <v-select
                    v-model="l.eleccion"
                    :items="opcionesLinea(l)"
                    item-text="texto" item-value="psp_id"
                    dense outlined hide-details
                    :placeholder="l.candidatos.length ? 'Elegí el producto…' : 'Sin coincidencias: agregalo con F6'"
                  ></v-select>
                  <div class="text-caption mt-1" :class="claseOrigen(l)">{{ textoOrigen(l) }}</div>
                </td>
                <td class="text-right">{{ l.cantidad }}</td>
                <td class="text-right">{{ moneda(l.precio_unitario) }}</td>
                <td class="text-right">{{ l.alicuota_iva !== null ? l.alicuota_iva + "%" : "—" }}</td>
                <td class="text-right">{{ moneda(l.subtotal) }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-row dense class="mt-3">
            <v-col cols="12" md="7" class="text-caption grey--text">
              <span v-if="lectura.totales.neto_gravado !== null">Neto gravado {{ moneda(lectura.totales.neto_gravado) }}</span>
              <span v-for="(iva, i) in lectura.totales.iva" :key="'iva' + i"> · IVA {{ iva.alicuota }}% {{ moneda(iva.monto) }}</span>
              <span v-for="(p, i) in lectura.totales.percepciones" :key="'p' + i"> · {{ p.concepto }} {{ moneda(p.monto) }}</span>
              <div class="mt-1">
                {{ resumenLineas.asignadas }} de {{ lineas.length }} líneas con producto asignado
                <span v-if="resumenLineas.sinAsignar"> · {{ resumenLineas.sinAsignar }} las agregás a mano con F6</span>
              </div>
            </v-col>
            <v-col cols="12" md="5" class="text-right">
              <div class="text-caption grey--text">Total del comprobante</div>
              <div class="text-h6 font-weight-bold">{{ moneda(lectura.totales.total) }}</div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <v-card-actions v-if="lectura">
        <v-btn text @click="reiniciar">Otro archivo</v-btn>
        <v-spacer></v-spacer>
        <span class="text-caption grey--text mr-3">{{ lectura.meta.modelo }} · {{ (lectura.meta.duracion_ms / 1000).toFixed(1) }} s</span>
        <v-btn depressed color="primary" :disabled="!proveedorId" @click="aplicar">
          Pasar al formulario
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";

const ORIGEN = {
  mapa: { texto: "Asociado en compras anteriores", clase: "green--text text--darken-2" },
  codigo: { texto: "Coincide el código del proveedor", clase: "green--text text--darken-2" },
  nombre: { texto: "Propuesto por el nombre: confirmalo", clase: "orange--text text--darken-3" },
  ia: { texto: "Propuesto por el asistente: confirmalo", clase: "orange--text text--darken-3" },
};

export default {
  name: "CompraDesdeArchivo",
  props: {
    // Opcional: sin uuid, el backend usa la sucursal del contexto (header X-Sucursal)
    sucursalUuid: { type: String, default: null },
  },
  data() {
    return {
      abierto: false,
      archivo: null,
      leyendo: false,
      error: "",
      lectura: null,
      proveedorId: null,
      lineas: [],
    };
  },
  computed: {
    numeroFormateado() {
      const c = this.lectura.comprobante;
      if (c.punto_venta === null && c.numero === null) return "sin número";
      const pv = c.punto_venta !== null ? String(c.punto_venta).padStart(4, "0") : "????";
      const n = c.numero !== null ? String(c.numero).padStart(8, "0") : "????????";
      return `${pv}-${n}`;
    },
    opcionesProveedor() {
      const p = this.lectura.proveedor;
      const items = [];
      if (p.id) items.push({ id: p.id, nombre: p.nombre });
      p.candidatos.forEach((c) => {
        if (!items.find((i) => i.id === c.id)) items.push(c);
      });
      return items;
    },
    resumenLineas() {
      const asignadas = this.lineas.filter((l) => l.eleccion).length;
      return { asignadas, sinAsignar: this.lineas.length - asignadas };
    },
  },
  methods: {
    moneda(v) {
      if (v === null || v === undefined) return "—";
      return "$ " + Number(v).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    async leer() {
      this.leyendo = true;
      this.error = "";
      const form = new FormData();
      form.append("archivo", this.archivo);
      if (this.sucursalUuid) form.append("sucursal", this.sucursalUuid);
      try {
        const { data } = await axios.post("/asistente/compras/leer", form, { headers: { "Content-Type": "multipart/form-data" } });
        this.lectura = data;
        this.proveedorId = data.proveedor.id;
        this.lineas = data.lineas.map((l) => ({
          ...l,
          eleccion: l.propuesta ? l.propuesta.psp_id : null,
        }));
      } catch (e) {
        const detalle = e.response && e.response.data && e.response.data.error;
        this.error = detalle || "No pude leer el archivo. Probá con otro o cargá la factura a mano.";
      } finally {
        this.leyendo = false;
      }
    },
    opcionesLinea(l) {
      const items = [];
      const agregar = (c) => {
        if (!c || !c.psp_id || items.find((i) => i.psp_id === c.psp_id)) return;
        items.push({ psp_id: c.psp_id, texto: `${c.descripcion} — ${c.presentacion}` });
      };
      agregar(l.propuesta);
      l.candidatos.forEach(agregar);
      // Candidatos que existen en la empresa pero no en esta sucursal no se pueden comprar acá
      return items;
    },
    estadoLinea(l) {
      if (!l.eleccion) return "sin-asignar";
      return l.propuesta && l.eleccion === l.propuesta.psp_id && l.confianza === "alta" ? "seguro" : "revisar";
    },
    textoOrigen(l) {
      if (!l.eleccion) return l.candidatos.length ? "Ninguna coincidencia clara" : "No está en tu catálogo";
      if (l.propuesta && l.eleccion === l.propuesta.psp_id) return ORIGEN[l.origen].texto;
      return "Elegido por vos";
    },
    claseOrigen(l) {
      if (!l.eleccion) return "red--text text--darken-1";
      if (l.propuesta && l.eleccion === l.propuesta.psp_id) return ORIGEN[l.origen].clase;
      return "grey--text";
    },
    productoDe(l) {
      const todos = [l.propuesta, ...l.candidatos].filter(Boolean);
      const item = todos.find((c) => c.psp_id === l.eleccion);
      return item || null;
    },
    aplicar() {
      const asignadas = [];
      const pendientes = [];
      this.lineas.forEach((l) => {
        const item = this.productoDe(l);
        if (item && item.producto) {
          asignadas.push({
            producto: item.producto,
            presentacion_id: item.presentacion_id,
            cantidad: Number(l.cantidad) || 1,
            precio_unitario: Number(l.precio_unitario) || 0,
            descuento_pct: l.descuento_pct,
            alicuota_iva: l.alicuota_iva,
            codigo: l.codigo || "",
            descripcion: l.descripcion,
          });
        } else {
          pendientes.push(l);
        }
      });
      this.$emit("aplicar", {
        comprobante: this.lectura.comprobante,
        proveedor_id: this.proveedorId,
        lineas: asignadas,
        pendientes,
        totales: this.lectura.totales,
        verificacion: this.lectura.verificacion,
      });
      this.cerrar();
    },
    reiniciar() {
      this.lectura = null;
      this.lineas = [];
      this.proveedorId = null;
      this.archivo = null;
      this.error = "";
    },
    cerrar() {
      this.abierto = false;
      this.reiniciar();
    },
  },
};
</script>

<style scoped>
.lineas tr.seguro td:nth-child(2) { border-left: 3px solid #43a047; }
.lineas tr.revisar td:nth-child(2) { border-left: 3px solid #fb8c00; }
.lineas tr.sin-asignar td:nth-child(2) { border-left: 3px solid #e53935; }
.lineas td { padding-top: 6px !important; padding-bottom: 6px !important; vertical-align: top; }
</style>
